import { findPrice } from '@epicuro-next/ataraxia/product-price';
import { getMostReorderedProductsHeading } from '@epicuro-next/constants/features/localization';
import { mapEngagementMediaToLegacy } from '@epicuro-next/data/engagement/engagement-api-utils';
import {
  EngagementMedia,
  MediaResponseDto,
} from '@epicuro-next/data/engagement/engagement-models';
import { isNonNullish, LinkInfo, urlToLinkInfo } from '@epicuro-next/utilities';
import { ProductPriceRanges } from 'graphql-api/gql-api-interfaces';
import { decode } from 'html-entities';

import { createHttpRequestFetchString } from './configs/http-request';
import {
  getPanelTrackingIdAsString,
  PanelTrackingIdConstants,
  PanelTrackingIdVariables,
} from './configs/panel-tracking-id';
import { parseSearchUrlToSearchQuery } from './configs/product-basket';
import { httpBodyToProductData } from './configs/product-data';
import { LoadableItemView } from './configs/types/item-view';
import {
  isResolveValueError,
  ResolveValueErrorType,
} from './configs/types/resolve-value-error';
import { getProductAsLoadableImageView } from './configs/views/product-as-image';
import { productCard } from './configs/views/product-card';
import { getNumberOrdinalSuffix } from './utils/number-ordinal-suffix';

declare global {
  interface Window {
    neoclesDynamicFramework: {
      recommendationsPanels: {
        // By the way the dynamic framework deals with transform functions (creating functions from strings) it is not straigth
        // foward to reference methods outside that transform method's own scope.
        // To be able to reuse method, the global Window object is used.
        // If you want to have access to a method for reusability inside a transform method, you should
        // add it to the `methods` object below, and define it where this `methods` object is initialized.
        methods: {
          createHttpRequestFetchString: typeof createHttpRequestFetchString;
          decode: typeof decode;
          text: {
            getMostReorderedProductsHeading: typeof getMostReorderedProductsHeading;
          };
          getPanelTrackingId: (
            constants: PanelTrackingIdConstants,
            variables: PanelTrackingIdVariables,
          ) => string;
          httpBodyToProductData: (
            // tslint:disable-next-line:no-any
            data: any,
            // tslint:disable-next-line:bool-param-default
            shuffle?: boolean,
          ) => unknown;
          isResolveValueError: (data: unknown) => data is ResolveValueErrorType;
          parseSearchUrlToQuery: typeof parseSearchUrlToSearchQuery;
          urlToLinkInfo: (urlWithOptionalQueryParams: string) => LinkInfo;
          getNumberOrdinalSuffix(number: number): string;
          findPrice(prices: ProductPriceRanges[]): ProductPriceRanges;
          getProductAsLoadableImageView(): LoadableItemView;
          createPCardView(): LoadableItemView;
          mapEngagementMediaToLegacy(media: MediaResponseDto): EngagementMedia;
          isNonNullish: <T>(item: T) => item is NonNullable<T>;
        };
      };
    };
  }
}

export function registerGloballyAvailableFunctions(): void {
  if (window.neoclesDynamicFramework === undefined) {
    window.neoclesDynamicFramework =
      {} as typeof window.neoclesDynamicFramework;
  }

  if (window.neoclesDynamicFramework.recommendationsPanels === undefined) {
    window.neoclesDynamicFramework.recommendationsPanels =
      {} as typeof window.neoclesDynamicFramework.recommendationsPanels;
  }

  if (
    window.neoclesDynamicFramework.recommendationsPanels.methods === undefined
  ) {
    window.neoclesDynamicFramework.recommendationsPanels.methods = {
      createHttpRequestFetchString,
      decode,
      getNumberOrdinalSuffix,
      getPanelTrackingId: getPanelTrackingIdAsString,
      httpBodyToProductData,
      isResolveValueError,
      parseSearchUrlToQuery: parseSearchUrlToSearchQuery,
      text: {
        getMostReorderedProductsHeading,
      },
      urlToLinkInfo,
      findPrice,
      getProductAsLoadableImageView,
      createPCardView: productCard,
      isNonNullish: isNonNullish,
      mapEngagementMediaToLegacy,
    };
  }
}
